import React from "react";
import { useTranslation } from "react-i18next";
import { BotMessageSquare } from "lucide-react";

import BetaBadge from "./BetaBadge";
import { openCrmChatDrawer } from "../CrmChatController";
import { useAskAiInItemDetailEnabled } from "../hooks/useAskAiInItemDetailEnabled";
import type { CrmRecordType } from "../types/chat.types";

interface AskAiButtonProps {
  workspaceId: string;
  recordId: string;
  recordType: CrmRecordType;
  displayName: string;
  subtitle?: string;
  avatarUrl?: string;
  promptType?: string;
  /** Accessible label override. */
  ariaLabel?: string;
  /** Extra classes appended to the button. */
  className?: string;
}

/**
 * Header-style trigger for the CRM Chat drawer. Used on the Account, Contact,
 * and Deal detail panels (and any future "Ask AI" entry points). Renders a
 * beta badge alongside the label while the feature is in preview.
 */
const AskAiButton: React.FC<AskAiButtonProps> = ({
  workspaceId,
  recordId,
  recordType,
  displayName,
  subtitle,
  avatarUrl,
  promptType,
  ariaLabel,
  className = "",
}) => {
  const { t } = useTranslation();
  const askEnabled = useAskAiInItemDetailEnabled();

  if (!askEnabled || !workspaceId || !recordId) return null;

  const handleClick = () => {
    openCrmChatDrawer({
      workspaceId,
      recordId,
      recordType,
      displayName,
      subtitle,
      avatarUrl,
      promptType,
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={ariaLabel ?? (t("Ask AI") as string)}
      className={`inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 ${className}`}
    >
      <BotMessageSquare className="w-3.5 h-3.5" />
      <span>{t("Ask AI")}</span>
      <BetaBadge className="ml-0.5" />
    </button>
  );
};

export default AskAiButton;
